'use client';

import { TrendDown, TrendUp } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * The one period-over-period change pill for the whole platform.
 *
 * Two independent facts, deliberately separated, because collapsing them is
 * what made the Code Review KPI row render an UP arrow beside "-79%":
 *
 *   direction: which way the NUMBER moved. Purely the sign.
 *   tone:      whether that movement is good, bad, or not worth a colour.
 *
 * For a latency metric those disagree by design (time to first review fell 79%,
 * which is the win), so the arrow follows the sign and the colour follows the
 * judgement. Neither one is allowed to overwrite the other.
 *
 * Colour comes from `Badge`, not from a local class map, so this pill can never
 * drift from the design system. `good` is the brand olive: a positive metric is
 * DATA, not a success STATE, and `ds-success-*` is a generic kelly green that
 * is not this product's green (design/tokens.md §9, and the eslint guard).
 * `bad` keeps the danger red, because a metric that got worse is the one case
 * on these dashboards where a status colour is the honest read.
 */

export type DeltaTone = 'good' | 'bad' | 'neutral';

/** Which way the number moved. `flat` prints no arrow. */
export type DeltaDirection = 'up' | 'down' | 'flat';

export interface Delta {
  text: string;
  tone: DeltaTone;
  direction: DeltaDirection;
}

export interface ToDeltaOptions {
  /**
   * Down is the win. Set for latency, error rate, cost -- anything where a
   * falling number is the good outcome. Only flips `tone`; `direction` still
   * follows the sign.
   */
  invert?: boolean;
  /**
   * Unit printed after the magnitude. `'%'` for a relative change, `'pp'` for a
   * change in percentage POINTS on a metric that is itself a percentage, where
   * "+2%" would be ambiguous and wrong, and `'none'` for a score that moves in
   * bare points and names its own window in the label ("+2.3 this week").
   */
  unit?: '%' | 'pp' | 'none';
}

/**
 * Signed change as a badge payload, or null when there is no comparison window
 * to speak of. Callers pass the raw signed number and say what it means; they
 * do not assemble the string or pick the tone themselves.
 */
export function toDelta(
  value: number | null | undefined,
  options: ToDeltaOptions | boolean = {}
): Delta | null {
  // `toDelta(x, true)` stayed valid when this moved out of the Code Review
  // dashboard, so the call sites did not all have to change in one commit.
  const { invert = false, unit = '%' } =
    typeof options === 'boolean' ? { invert: options } : options;

  if (value === null || value === undefined || Number.isNaN(value)) return null;

  // Percentage points read at one decimal at every size; a relative percent
  // drops the decimal once it is big enough not to need it.
  const rounded =
    unit === '%' && Math.abs(value) >= 10 ? value.toFixed(0) : value.toFixed(1);
  const magnitude = Math.abs(Number(rounded));
  if (magnitude === 0)
    return { text: 'No change', tone: 'neutral', direction: 'flat' };

  const suffix = unit === 'pp' ? ' pp' : unit === 'none' ? '' : '%';
  const isGood = invert ? value < 0 : value > 0;
  return {
    text: `${value > 0 ? '+' : '-'}${magnitude}${suffix}`,
    tone: isGood ? 'good' : 'bad',
    direction: value > 0 ? 'up' : 'down',
  };
}

/** Tone -> the `Badge` variant that carries it. */
const TONE_VARIANT: Record<DeltaTone, 'default' | 'destructive' | 'secondary'> =
  {
    good: 'default',
    bad: 'destructive',
    neutral: 'secondary',
  };

/**
 * `default` is the brand olive at `text-ds-brand-default`, which is the full
 * strength ink. A pill is small and repeats across a row, so it steps down to
 * `subtle` -- the same weight `UsageStatusBadge` uses in Agent Insights, so the
 * two dashboards render the same pill.
 */
const TONE_CLASS: Record<DeltaTone, string> = {
  good: 'text-ds-brand-subtle',
  bad: '',
  neutral: '',
};

export function DeltaBadge({
  delta,
  className,
  ...rest
}: {
  delta: Delta;
  className?: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>) {
  return (
    <Badge
      variant={TONE_VARIANT[delta.tone]}
      className={cn(
        'shrink-0 gap-0.5 px-1.5 py-0 tabular-nums whitespace-nowrap',
        TONE_CLASS[delta.tone],
        className
      )}
      {...rest}
    >
      {delta.direction === 'up' ? (
        <TrendUp size={12} weight="bold" aria-hidden />
      ) : delta.direction === 'down' ? (
        <TrendDown size={12} weight="bold" aria-hidden />
      ) : null}
      {delta.text}
    </Badge>
  );
}
